import {
	SofaConfiguration,
	SofaConfigurationItem,
	RangeSettings,
	CoverItemSetting,
	SofaConfigurationItemICF,
} from 'sofa-reborn-react';
import { ProductData, ModelData, Groups, Product } from '@ikea-aoa/component-view-3d';

let id = 0;

const configurationService = {
	getFormattedDate: (): string => {
		// Datetime, '%Y-%m-%dT%H:%M:%SZ', '2021-01-01T01:01:01Z'
		return new Date().toISOString();
	},
	getCoverItem: (
		product: Product | null,
		rangeSettings: RangeSettings
	): CoverItemSetting | null => {
		if (product) {
			const coverItem = rangeSettings.coverItems.find((item) => {
				return item.coverName === product.filter.color;
			});

			return coverItem || null;
		}
		return null;
	},
	getGroupCategory: (modelId: string, groups: Groups): string => {
		let groupCategory = '';
		if (groups) {
			Object.keys(groups).forEach((group) => {
				if (groups[group].models.includes(modelId)) {
					groupCategory = group;
				}
			});
		}

		return groupCategory;
	},
	getICFArticle: (
		configurationItem: SofaConfigurationItem,
		parentId: number | null,
		productData: ProductData,
		models: ModelData,
		groups: Groups,
		rangeSettings: RangeSettings
	): SofaConfigurationItemICF[] => {
		if (!configurationItem.articleId || !parseInt(configurationItem.articleId)) {
			return [];
		}
		const product =
			configurationItem && configurationItem.articleId
				? productData[configurationItem.articleId]
				: null;
		const modelId =
			configurationItem && configurationItem.articleId
				? productData[configurationItem.articleId].modelId
				: '';
		const coverItem = configurationService.getCoverItem(product, rangeSettings);

		const getRotation = (rotation) => (rotation < 0 ? 360 + (rotation % 360) : rotation) % 360;

		const ICFArticle: SofaConfigurationItemICF[] = [
			{
				id: ++id,
				parent_ids: parentId ? [parentId] : [],
				child_ids: [],
				product_id: configurationItem.articleId || '',
				name: modelId,
				category: configurationService.getGroupCategory(modelId, groups),
				colors: coverItem ? [coverItem.color] : [''],
				attributes: {
					color_name: coverItem ? coverItem.coverName : '',
				},
				dimensions: {
					x: models[modelId].bounds.default.size.width * 100,
					y: models[modelId].bounds.default.size.height * 100,
					z: models[modelId].bounds.default.size.depth * 100,
				},
				transform: {
					position: {
						x:
							configurationItem && configurationItem.pos && configurationItem.pos.x
								? configurationItem.pos.x * 10
								: 0,
						y:
							configurationItem && configurationItem.pos && configurationItem.pos.y
								? configurationItem.pos.y * 10
								: 0,
						z:
							configurationItem && configurationItem.pos && configurationItem.pos.z
								? configurationItem.pos.z * 10
								: 0,
					},
					rotation: {
						x:
							configurationItem.rot && configurationItem.rot.rx
								? getRotation(configurationItem.rot.rx)
								: 0,
						y:
							configurationItem.rot && configurationItem.rot.ry
								? getRotation(configurationItem.rot.ry)
								: 0,
						z:
							configurationItem.rot && configurationItem.rot.rz
								? getRotation(configurationItem.rot.rz)
								: 0,
					},
					scale: { x: 1.0, y: 1.0, z: 1.0 },
				},
			},
		];

		if (configurationItem.children) {
			const parentId = id;
			const ICFChildArticleIds: number[] = [];
			configurationItem.children.forEach((child) => {
				// Remove backrest for Jattebo as it is included
				if (child.articleId && !child.articleId.includes('backrest')) {
					const ICFChildArticle = configurationService.getICFArticle(
						child,
						parentId,
						productData,
						models,
						groups,
						rangeSettings
					) as SofaConfigurationItemICF[];
					if (ICFChildArticle.length > 0) {
						ICFChildArticleIds.push(ICFChildArticle[0].id);
						ICFArticle.push(...ICFChildArticle);
					}
				} else {
					// Backrest at back position, add depth to parent
					if (child.rot?.ry === 0) {
						const modelId = child && child.articleId ? productData[child.articleId].modelId : '';
						ICFArticle[0].dimensions.z += models[modelId].bounds.default.size.depth * 100;
					}
				}
			});

			ICFArticle[0].child_ids = ICFChildArticleIds;
		}

		return ICFArticle;
	},
	getICFArticles: (
		configurationChildren: SofaConfigurationItem[],
		productData: ProductData,
		models: ModelData,
		groups: Groups,
		rangeSettings: RangeSettings
	): SofaConfigurationItemICF[] => {
		const formattedConfigurationChildren = [];
		configurationChildren.forEach((child) => {
			formattedConfigurationChildren.push(
				...(configurationService.getICFArticle(
					child,
					null,
					productData,
					models,
					groups,
					rangeSettings
				) as never[])
			);
		});

		return formattedConfigurationChildren;
	},
	getICF: (
		sofaConfiguration: SofaConfiguration,
		productData: ProductData,
		models: ModelData,
		groups: Groups,
		rangeSettings: RangeSettings
		// eslint-disable-next-line @typescript-eslint/ban-types
	): Record<string, object> => {
		return {
			icf_version: new String('1.0'),
			application_id: new String(rangeSettings.rangeId || 'vimle'),
			creation_datetime: new String(configurationService.getFormattedDate()),
			articles: configurationService.getICFArticles(
				sofaConfiguration.configuration.children,
				productData,
				models,
				groups,
				rangeSettings
			),
		};
	},
};
export default configurationService;
